import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ChevronDown,
  ChevronUp,
  CreditCard,
  ExternalLink,
  Eye,
  MapPin,
  Maximize2,
  Package,
  PackageCheck,
  Phone,
  RefreshCw,
  Search,
  Truck,
  X,
  XCircle,
} from "lucide-react";

import { apiGet, apiPatch } from "@/services/api";
import { formatPrice } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { AdminPagination } from "@/components/admin/admin-pagination";
import { ItemVariantBadge, resolveItemVariant } from "@/components/common/item-variant-badge";

type VendorOrder = {
  id: string;
  status: string;
  subtotal: number;
  commissionAmount?: number;
  netAmount: number;
  createdAt: string;
  items: {
    id: string;
    title: string;
    quantity: number;
    price?: number;
    image?: string;
    variantId?: string | null;
    variantTitle?: string | null;
    sku?: string | null;
    product?: {
      id?: string;
      title?: string;
      slug?: string;
      category?: string;
      brand?: string;
      variants?: any[];
      specifications?: Record<string, any>;
    } | null;
  }[];
  order: {
    id: string;
    shippingFullName: string;
    shippingPhone?: string;
    shippingLine1?: string;
    shippingCity?: string;
    shippingState?: string;
    paymentStatus: string;
  } | null;
};

const STATUSES = [
  "PENDING",
  "ACCEPTED",
  "PROCESSING",
  "READY_TO_SHIP",
  "SHIPPED",
  "DELIVERED",
  "CANCELLED",
];

function statusTone(status: string): "success" | "deal" | "prime" | "default" {
  if (status === "DELIVERED") return "success";
  if (status === "CANCELLED") return "deal";
  if (["ACCEPTED", "PROCESSING", "READY_TO_SHIP", "SHIPPED"].includes(status)) return "prime";
  return "default";
}

function getCategoryBadge(category?: string | null) {
  const cat = (category || "").toLowerCase();
  if (cat.includes("phone") || cat.includes("mobile")) {
    return {
      label: "📱 Mobiles & Accessories",
      color: "bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-950/60 dark:text-sky-300 dark:border-sky-800/40",
    };
  }
  if (cat.includes("laptop") || cat.includes("computer") || cat.includes("pc")) {
    return {
      label: "💻 Laptops & PC",
      color: "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-950/60 dark:text-purple-300 dark:border-purple-800/40",
    };
  }
  if (cat.includes("audio") || cat.includes("airpod") || cat.includes("earbud") || cat.includes("headphone")) {
    return {
      label: "🎧 Audio & Earbuds",
      color: "bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-950/60 dark:text-teal-300 dark:border-teal-800/40",
    };
  }
  if (cat.includes("watch") || cat.includes("smart")) {
    return {
      label: "⌚ Wearables",
      color: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800/40",
    };
  }
  if (cat.includes("electronic")) {
    return {
      label: "⚡ Electronics",
      color: "bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-950/60 dark:text-indigo-300 dark:border-indigo-800/40",
    };
  }
  return {
    label: category || "General Merchandise",
    color: "bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-white/10",
  };
}

export function VendorOrdersPage() {
  const [orders, setOrders] = useState<VendorOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailModalOrder, setDetailModalOrder] = useState<VendorOrder | null>(null);
  const [enlargedImage, setEnlargedImage] = useState<{ url: string; title: string } | null>(null);

  async function load() {
    setLoading(true);
    try {
      const d = await apiGet<{ vendorOrders: VendorOrder[] }>("/api/vendors/me/orders");
      setOrders(d?.vendorOrders ?? []);
    } catch {
      toast.error("Failed to load vendor orders");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function setStatus(id: string, status: string) {
    try {
      await apiPatch(`/api/vendors/me/orders/${id}/status`, { status });
      toast.success("Order status updated to " + status);
      setOrders((prev) =>
        prev.map((o) => (o.id === id ? { ...o, status } : o))
      );
    } catch {
      toast.error("Could not update order status");
    }
  }

  const activeModalOrder = useMemo(() => {
    if (!detailModalOrder) return null;
    return orders.find((o) => o.id === detailModalOrder.id) || detailModalOrder;
  }, [orders, detailModalOrder]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      const matchesSearch =
        !q ||
        o.id.toLowerCase().includes(q) ||
        (o.order?.id ?? "").toLowerCase().includes(q) ||
        (o.order?.shippingFullName ?? "").toLowerCase().includes(q) ||
        (o.order?.shippingPhone ?? "").includes(q) ||
        (o.order?.shippingCity ?? "").toLowerCase().includes(q) ||
        (o.order?.shippingState ?? "").toLowerCase().includes(q) ||
        o.items.some((i) => {
          const vInfo = resolveItemVariant(i);
          return (
            i.title.toLowerCase().includes(q) ||
            (i.variantTitle ?? "").toLowerCase().includes(q) ||
            (vInfo.color ?? "").toLowerCase().includes(q) ||
            (vInfo.size ?? "").toLowerCase().includes(q) ||
            (i.product?.category ?? "").toLowerCase().includes(q) ||
            (i.product?.brand ?? "").toLowerCase().includes(q)
          );
        });
      const matchesStatus = statusFilter === "ALL" || o.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [orders, search, statusFilter]);

  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#073B70] dark:text-white">Customer Orders</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Manage your store orders, track dispatch status, and review customer delivery details.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 active:scale-95 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200 cursor-pointer"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-3.5 sm:p-5 shadow-2xs dark:border-white/10 dark:bg-slate-900">
          <PackageCheck className="h-5 w-5 sm:h-6 sm:w-6 text-amazon-teal" />
          <p className="mt-2 sm:mt-3 text-[10px] sm:text-xs font-black uppercase text-slate-500">Total store orders</p>
          <p className="mt-0.5 sm:mt-1 text-2xl sm:text-3xl font-black tracking-normal text-slate-900 dark:text-white">
            {orders.length}
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3.5 sm:p-5 shadow-2xs dark:border-white/10 dark:bg-slate-900">
          <Truck className="h-5 w-5 sm:h-6 sm:w-6 text-amazon-orange" />
          <p className="mt-2 sm:mt-3 text-[10px] sm:text-xs font-black uppercase text-slate-500">Dispatch Queue</p>
          <p className="mt-0.5 sm:mt-1 text-2xl sm:text-3xl font-black tracking-normal text-slate-900 dark:text-white">
            {
              orders.filter((o) =>
                ["PENDING", "ACCEPTED", "PROCESSING", "READY_TO_SHIP"].includes(o.status)
              ).length
            }
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3.5 sm:p-5 shadow-2xs dark:border-white/10 dark:bg-slate-900">
          <CreditCard className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
          <p className="mt-2 sm:mt-3 text-[10px] sm:text-xs font-black uppercase text-slate-500">Net Earnings</p>
          <p className="mt-0.5 sm:mt-1 text-2xl sm:text-3xl font-black tracking-normal text-emerald-600 dark:text-emerald-400">
            {formatPrice(
              orders
                .filter((o) => o.status !== "CANCELLED")
                .reduce((sum, o) => sum + (o.netAmount || 0), 0)
            )}
          </p>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3">
        <div className="relative min-w-48 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search by order ID, customer, phone, product title…"
            className="h-10 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-8 text-sm outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
          />
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setPage(1);
              }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="h-10 flex-1 sm:flex-none rounded-xl border border-slate-200 bg-white px-3 text-xs sm:text-sm outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900 font-semibold text-slate-800 dark:text-slate-200"
          >
            <option value="ALL">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <span className="text-xs text-slate-500 whitespace-nowrap">
            {filtered.length} order{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Orders List Container */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xs dark:border-white/10 dark:bg-slate-900">
        {/* MOBILE CARD VIEW (< md) */}
        <div className="block md:hidden divide-y divide-slate-100 dark:divide-white/5">
          {loading && orders.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <RefreshCw className="mx-auto h-6 w-6 animate-spin text-slate-400 mb-2" />
              Loading orders...
            </div>
          ) : paginated.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Package className="mx-auto h-8 w-8 text-slate-300 dark:text-slate-600 mb-1.5" />
              <p className="font-bold text-slate-700 dark:text-slate-300">No orders found</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Customer orders placed for your products will show up here.
              </p>
            </div>
          ) : (
            paginated.map((order) => {
              const totalItems = order.items.reduce((sum, item) => sum + item.quantity, 0);
              const firstItem = order.items[0];

              return (
                <div key={order.id} className="p-3.5 space-y-2.5">
                  {/* Top: Order # & Status Badge */}
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <span className="font-mono text-xs font-black text-slate-950 dark:text-white">
                        #{order.id.slice(-8).toUpperCase()}
                      </span>
                      <span className="text-[10px] text-slate-400 ml-1.5">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <Badge tone={statusTone(order.status)}>{order.status}</Badge>
                  </div>

                  {/* Customer line */}
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900 dark:text-white truncate">
                        {order.order?.shippingFullName || "Customer"}
                      </p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {[order.order?.shippingCity, order.order?.shippingState]
                          .filter(Boolean)
                          .join(", ") || "Addis Ababa"}
                      </p>
                    </div>

                    {order.order?.shippingPhone && (
                      <a
                        href={`tel:${order.order.shippingPhone}`}
                        className="shrink-0 inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40"
                      >
                        <Phone className="h-3 w-3" />
                        <span>Call</span>
                      </a>
                    )}
                  </div>

                  {/* Items preview snippet */}
                  <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-2 dark:border-white/5 dark:bg-slate-800/40">
                    <div className="flex items-center gap-2.5">
                      {firstItem?.image ? (
                        <img
                          src={firstItem.image}
                          alt={firstItem.title}
                          className="h-10 w-10 shrink-0 rounded-lg object-contain bg-white border border-slate-200 dark:border-white/10 p-0.5"
                        />
                      ) : (
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-400">
                          <Package className="h-4 w-4" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-slate-900 dark:text-white truncate">
                          {firstItem?.title || "Product item"}
                        </p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                          {totalItems} item{totalItems !== 1 ? "s" : ""} · Net:{" "}
                          <strong className="font-extrabold text-emerald-600 dark:text-emerald-400">
                            {formatPrice(order.netAmount)}
                          </strong>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Bottom: Status Updater + See Detail Button */}
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      <select
                        value={order.status}
                        onChange={(e) => setStatus(order.id, e.target.value)}
                        className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs font-semibold outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-800 text-slate-800 dark:text-slate-200"
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>

                    <button
                      type="button"
                      onClick={() => setDetailModalOrder(order)}
                      className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-[#073B70]/25 bg-[#073B70]/10 px-3 py-1.5 text-xs font-bold text-[#073B70] hover:bg-[#073B70] hover:text-white transition-all shadow-2xs dark:border-[#087DB5]/40 dark:bg-[#087DB5]/20 dark:text-sky-300 dark:hover:bg-[#087DB5] dark:hover:text-white active:scale-95 cursor-pointer"
                    >
                      <Eye className="h-3.5 w-3.5" />
                      <span>Details</span>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* DESKTOP TABLE VIEW (>= md) */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full min-w-[800px] text-left text-sm">
            <thead className="bg-slate-100 text-xs uppercase text-slate-500 dark:bg-white/10">
              <tr>
                <th className="w-8 px-4 py-3" />
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Net Earnings</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Update</th>
                <th className="px-4 py-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-white/10">
              {loading && orders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                    <RefreshCw className="mx-auto h-6 w-6 animate-spin text-slate-400 mb-2" />
                    Loading orders...
                  </td>
                </tr>
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                    <Package className="mx-auto h-8 w-8 text-slate-300 dark:text-slate-600 mb-1.5" />
                    <p className="font-bold text-slate-700 dark:text-slate-300">No orders found</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Customer orders placed for your products will show up here.
                    </p>
                  </td>
                </tr>
              ) : (
                paginated.flatMap((order) => {
                  const isExpanded = expandedId === order.id;
                  const totalItems = order.items.reduce((sum, item) => sum + item.quantity, 0);

                  return [
                    <tr
                      key={order.id}
                      className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                    >
                      <td className="px-4 py-3 align-top">
                        <button
                          type="button"
                          onClick={() => setExpandedId(isExpanded ? null : order.id)}
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 cursor-pointer"
                          title={isExpanded ? "Collapse" : "Show details"}
                        >
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <p className="font-mono text-xs font-black text-slate-950 dark:text-white">
                          #{order.id.slice(-8).toUpperCase()}
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </p>
                        <span
                          className="inline-block mt-1 rounded bg-slate-100 dark:bg-white/10 px-1.5 py-0.5 text-[10px] font-mono text-slate-500 truncate max-w-[110px]"
                          title={order.id}
                        >
                          {order.id}
                        </span>
                        <div className="mt-1">
                          <span className="inline-flex items-center rounded-md bg-[#073B70]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#073B70] dark:bg-[#087DB5]/20 dark:text-sky-300">
                            {totalItems} item{totalItems !== 1 ? "s" : ""}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <p className="font-bold text-slate-900 dark:text-white">
                          {order.order?.shippingFullName || "Customer"}
                        </p>
                        {order.order?.shippingPhone && (
                          <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mt-0.5 flex items-center gap-1">
                            <span>📞</span>
                            <a
                              href={`tel:${order.order.shippingPhone}`}
                              className="hover:underline hover:text-[#087DB5]"
                            >
                              {order.order.shippingPhone}
                            </a>
                          </p>
                        )}
                        <p className="text-xs text-slate-400 mt-0.5">
                          {[order.order?.shippingCity, order.order?.shippingState]
                            .filter(Boolean)
                            .join(", ") || "Addis Ababa"}
                        </p>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <p className="font-bold text-emerald-600 dark:text-emerald-400 text-sm">
                          {formatPrice(order.netAmount)}
                        </p>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                          <span>Gross: {formatPrice(order.subtotal)}</span>
                          {order.commissionAmount ? (
                            <span className="ml-1 text-slate-400">
                              (Fee: -{formatPrice(order.commissionAmount)})
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <Badge tone={statusTone(order.status)}>{order.status}</Badge>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <select
                          value={order.status}
                          onChange={(e) => setStatus(order.id, e.target.value)}
                          className="h-9 rounded-md border border-slate-200 bg-transparent px-2 text-xs font-semibold outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-800"
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 align-top text-center whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setDetailModalOrder(order)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-[#073B70]/25 bg-[#073B70]/10 px-3.5 py-1.5 text-xs font-bold text-[#073B70] hover:bg-[#073B70] hover:text-white transition-all shadow-2xs dark:border-[#087DB5]/40 dark:bg-[#087DB5]/20 dark:text-sky-300 dark:hover:bg-[#087DB5] dark:hover:text-white active:scale-95 cursor-pointer"
                          title="See full order and product details"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          <span>See Detail</span>
                        </button>
                      </td>
                    </tr>,

                    isExpanded && (
                      <tr key={`${order.id}-detail`} className="bg-slate-50 dark:bg-white/[0.02]">
                        <td colSpan={7} className="px-6 py-5">
                          {order.status === "CANCELLED" && (
                            <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50/90 p-3.5 text-sm dark:border-rose-900/40 dark:bg-rose-950/30">
                              <div className="flex items-center gap-2">
                                <XCircle className="h-4 w-4 text-rose-600 dark:text-rose-400 shrink-0" />
                                <span className="font-bold text-rose-900 dark:text-rose-200">
                                  This order has been cancelled.
                                </span>
                              </div>
                            </div>
                          )}

                          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                            {/* Shipping address */}
                            <div>
                              <p className="mb-2 text-xs font-black uppercase tracking-wider text-slate-400">
                                Delivery destination
                              </p>
                              <p className="font-bold text-slate-900 dark:text-white">
                                {order.order?.shippingFullName || "Customer"}
                              </p>
                              <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
                                {order.order?.shippingLine1 || "—"}
                              </p>
                              <p className="text-sm text-slate-600 dark:text-slate-300">
                                {[order.order?.shippingCity, order.order?.shippingState]
                                  .filter(Boolean)
                                  .join(", ") || "Addis Ababa"}
                              </p>
                              {order.order?.shippingPhone && (
                                <p className="mt-1.5 flex items-center gap-1.5 text-sm font-bold text-slate-700 dark:text-slate-200">
                                  <span>📞</span>
                                  <a
                                    href={`tel:${order.order.shippingPhone}`}
                                    className="hover:underline hover:text-[#087DB5]"
                                  >
                                    {order.order.shippingPhone}
                                  </a>
                                </p>
                              )}
                            </div>

                            {/* Financial breakdown */}
                            <div>
                              <p className="mb-2 text-xs font-black uppercase tracking-wider text-slate-400">
                                Financial breakdown
                              </p>
                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Gross Sales</span>
                                  <span>{formatPrice(order.subtotal)}</span>
                                </div>
                                {order.commissionAmount ? (
                                  <div className="flex justify-between text-rose-600 dark:text-rose-400">
                                    <span>Platform Fee</span>
                                    <span>-{formatPrice(order.commissionAmount)}</span>
                                  </div>
                                ) : null}
                                <div className="flex justify-between border-t pt-1.5 font-bold text-emerald-600 dark:text-emerald-400 dark:border-white/10">
                                  <span>Your Net Earnings</span>
                                  <span>{formatPrice(order.netAmount)}</span>
                                </div>
                              </div>
                              <p className="mt-2 text-xs text-slate-400">
                                Payment:{" "}
                                <span className="font-bold text-slate-600 dark:text-slate-300">
                                  {order.order?.paymentStatus || "PAID"}
                                </span>
                              </p>
                            </div>

                            {/* Detailed Products summary */}
                            <div>
                              <div className="mb-2 flex items-center justify-between">
                                <span className="text-xs font-black uppercase tracking-wider text-slate-400">
                                  Ordered Products ({order.items.length})
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setDetailModalOrder(order)}
                                  className="text-[11px] font-bold text-[#087DB5] hover:underline flex items-center gap-1 cursor-pointer"
                                >
                                  <Eye className="h-3.5 w-3.5" /> Open Detail Modal
                                </button>
                              </div>
                              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                                {order.items.map((item) => {
                                  const badge = getCategoryBadge(item.product?.category);
                                  return (
                                    <div
                                      key={item.id}
                                      className="rounded-xl border border-slate-200 bg-white p-2.5 shadow-2xs dark:border-white/10 dark:bg-slate-800"
                                    >
                                      <div className="flex items-start gap-2.5">
                                        {item.image ? (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setEnlargedImage({
                                                url: item.image!,
                                                title: item.title,
                                              })
                                            }
                                            className="group relative h-12 w-12 shrink-0 rounded-lg object-contain bg-white border border-slate-200 dark:border-white/10 p-0.5 hover:border-[#087DB5] transition-all overflow-hidden cursor-zoom-in"
                                            title="Click to view large image"
                                          >
                                            <img
                                              src={item.image}
                                              alt={item.title}
                                              className="h-full w-full object-contain group-hover:scale-105 transition-transform"
                                            />
                                            <div className="absolute inset-0 bg-black/35 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white">
                                              <Maximize2 className="h-3.5 w-3.5" />
                                            </div>
                                          </button>
                                        ) : (
                                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-400 dark:bg-slate-700">
                                            <Package className="h-4 w-4" />
                                          </div>
                                        )}
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-start justify-between gap-1.5">
                                            <p className="text-xs font-bold leading-snug text-slate-900 dark:text-white line-clamp-1">
                                              {item.title}
                                            </p>
                                            {item.price && (
                                              <span className="shrink-0 text-xs font-bold text-slate-900 dark:text-white">
                                                {formatPrice(item.price * item.quantity)}
                                              </span>
                                            )}
                                          </div>

                                          <div className="mt-1 flex flex-wrap items-center gap-1">
                                            <span
                                              className={`inline-flex items-center rounded border px-1.5 py-0.2 text-[9.5px] font-bold ${badge.color}`}
                                            >
                                              {badge.label}
                                            </span>
                                            <span className="text-[10px] font-semibold text-slate-600 dark:text-slate-300">
                                              ×{item.quantity}
                                            </span>
                                            <ItemVariantBadge
                                              item={item}
                                              size="sm"
                                              showDefaultFallback={false}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ),
                  ];
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <AdminPagination
          currentPage={page}
          totalItems={filtered.length}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          itemLabel="orders"
        />
      </div>

      {/* Order & Product Details Modal */}
      {activeModalOrder && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4 backdrop-blur-xs animate-in fade-in duration-200"
          onClick={() => setDetailModalOrder(null)}
        >
          <div
            className="relative flex max-h-[92vh] sm:max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-2xl dark:border dark:border-white/10 dark:bg-slate-900"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 sm:px-6 sm:py-4 dark:border-white/10">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#073B70]/10 text-[#073B70] dark:bg-[#087DB5]/20 dark:text-sky-300">
                  <Package className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base sm:text-lg font-black text-slate-900 dark:text-white">
                      Order #{activeModalOrder.id.slice(-8).toUpperCase()}
                    </h3>
                    <Badge tone={statusTone(activeModalOrder.status)}>
                      {activeModalOrder.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Placed on {new Date(activeModalOrder.createdAt).toLocaleString()} · ID:{" "}
                    <span className="font-mono text-slate-700 dark:text-slate-300">
                      {activeModalOrder.id}
                    </span>
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDetailModalOrder(null)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white transition-colors cursor-pointer"
                title="Close modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-3.5 sm:p-6 space-y-4 sm:space-y-6">
              {/* Ordered Products Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                    <Package className="h-4 w-4 text-[#087DB5]" />
                    Ordered Products ({activeModalOrder.items.length})
                  </h4>
                  <span className="text-xs text-slate-500">
                    Total Qty:{" "}
                    <strong className="text-slate-800 dark:text-slate-200 font-bold">
                      {activeModalOrder.items.reduce((s, i) => s + i.quantity, 0)}
                    </strong>
                  </span>
                </div>

                <div className="space-y-3">
                  {activeModalOrder.items.map((item) => {
                    const badge = getCategoryBadge(item.product?.category);
                    const vInfo = resolveItemVariant(item);

                    return (
                      <div
                        key={item.id}
                        className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 sm:p-4 transition-all hover:border-[#087DB5]/40 hover:bg-white dark:border-white/10 dark:bg-slate-800/60 dark:hover:bg-slate-800"
                      >
                        <div className="flex flex-col sm:flex-row items-start gap-4">
                          {/* 128px high-resolution product image */}
                          <div className="relative h-28 w-28 sm:h-32 sm:w-32 shrink-0 rounded-xl bg-white border border-slate-200 dark:border-white/10 p-1.5 shadow-sm overflow-hidden group">
                            {item.image ? (
                              <>
                                <img
                                  src={item.image}
                                  alt={item.title}
                                  className="h-full w-full object-contain group-hover:scale-105 transition-transform"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    setEnlargedImage({ url: item.image!, title: item.title })
                                  }
                                  className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white cursor-zoom-in"
                                  title="Click to view large image"
                                >
                                  <Maximize2 className="h-5 w-5 drop-shadow" />
                                </button>
                              </>
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-slate-300 dark:text-slate-600">
                                <Package className="h-8 w-8" />
                              </div>
                            )}
                          </div>

                          {/* Product Details */}
                          <div className="min-w-0 flex-1 space-y-2">
                            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                              <div>
                                <h4 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white leading-snug">
                                  {item.title}
                                </h4>
                                {item.product?.slug && (
                                  <a
                                    href={`/products/${item.product.slug}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#087DB5] hover:underline mt-0.5"
                                  >
                                    <span>View Product in Store</span>
                                    <ExternalLink className="h-3 w-3" />
                                  </a>
                                )}
                              </div>
                              {item.price ? (
                                <div className="sm:text-right shrink-0">
                                  <span className="text-base font-black text-slate-900 dark:text-white">
                                    {formatPrice(item.price * item.quantity)}
                                  </span>
                                  <p className="text-xs text-slate-500">
                                    {formatPrice(item.price)} × {item.quantity}
                                  </p>
                                </div>
                              ) : null}
                            </div>

                            {/* Badges: Category, Brand, Color Swatch, Size */}
                            <div className="flex flex-wrap items-center gap-2 pt-1">
                              <span
                                className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-bold ${badge.color}`}
                              >
                                {badge.label}
                              </span>

                              {item.product?.brand && (
                                <span className="inline-flex items-center rounded-md bg-slate-200/80 px-2 py-0.5 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                                  Brand: {item.product.brand}
                                </span>
                              )}

                              {/* Highlighted Color with Circle Swatch */}
                              {vInfo.color ? (
                                <div className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-0.5 text-xs font-bold text-slate-900 shadow-2xs dark:border-white/10 dark:bg-slate-700 dark:text-white">
                                  <span
                                    className="h-3.5 w-3.5 rounded-full border border-black/20 shadow-xs shrink-0"
                                    style={{ backgroundColor: vInfo.colorCode || "#64748B" }}
                                  />
                                  <span>
                                    Color: <strong className="font-extrabold">{vInfo.color}</strong>
                                  </span>
                                </div>
                              ) : (
                                <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-500 dark:bg-slate-700 dark:text-slate-400">
                                  Standard Color Edition
                                </span>
                              )}

                              {/* Size Badge */}
                              {vInfo.size && (
                                <span className="inline-flex items-center rounded-md border border-slate-300 bg-slate-100 px-2.5 py-0.5 text-xs font-bold text-slate-800 dark:border-white/10 dark:bg-slate-700 dark:text-slate-100">
                                  Size: {vInfo.size}
                                </span>
                              )}

                              {item.sku && (
                                <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[11px] text-slate-500 dark:bg-slate-700 dark:text-slate-400">
                                  SKU: {item.sku}
                                </span>
                              )}
                            </div>

                            {item.variantTitle && (
                              <div className="text-xs text-slate-500 dark:text-slate-400 pt-1 border-t border-slate-200/60 dark:border-white/5">
                                Variant Tag:{" "}
                                <strong className="text-slate-700 dark:text-slate-300 font-medium">
                                  {item.variantTitle}
                                </strong>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Customer, Shipping, Financial Details Grid */}
              <div className="grid gap-4 sm:grid-cols-2">
                {/* Shipping Details */}
                <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-800">
                  <h4 className="text-xs font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-2.5">
                    <MapPin className="h-3.5 w-3.5 text-[#087DB5]" />
                    Delivery Destination & Customer
                  </h4>
                  <p className="font-bold text-sm text-slate-900 dark:text-white">
                    {activeModalOrder.order?.shippingFullName || "Customer"}
                  </p>
                  <p className="mt-1 text-xs text-slate-700 dark:text-slate-300">
                    {activeModalOrder.order?.shippingLine1 || "—"}
                  </p>
                  <p className="text-xs text-slate-700 dark:text-slate-300">
                    {[activeModalOrder.order?.shippingCity, activeModalOrder.order?.shippingState]
                      .filter(Boolean)
                      .join(", ") || "Addis Ababa"}
                  </p>
                  {activeModalOrder.order?.shippingPhone && (
                    <p className="mt-2 text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                      <Phone className="h-3.5 w-3.5 text-emerald-600" />
                      <a
                        href={`tel:${activeModalOrder.order.shippingPhone}`}
                        className="hover:underline hover:text-[#087DB5]"
                      >
                        {activeModalOrder.order.shippingPhone}
                      </a>
                    </p>
                  )}
                </div>

                {/* Financial Breakdown */}
                <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-800">
                  <h4 className="text-xs font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-2.5">
                    <CreditCard className="h-3.5 w-3.5 text-[#087DB5]" />
                    Financial Breakdown
                  </h4>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between text-slate-600 dark:text-slate-300">
                      <span>Gross Sales</span>
                      <span className="font-semibold">
                        {formatPrice(activeModalOrder.subtotal)}
                      </span>
                    </div>
                    {activeModalOrder.commissionAmount ? (
                      <div className="flex justify-between text-rose-600 dark:text-rose-400">
                        <span>Platform Commission Fee</span>
                        <span>-{formatPrice(activeModalOrder.commissionAmount)}</span>
                      </div>
                    ) : null}
                    <div className="flex justify-between border-t border-slate-200 pt-2 text-sm font-black text-emerald-600 dark:text-emerald-400 dark:border-white/10">
                      <span>Your Net Earnings</span>
                      <span>{formatPrice(activeModalOrder.netAmount)}</span>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-slate-500 flex items-center gap-1">
                    Payment Status:{" "}
                    <span className="font-bold text-slate-800 dark:text-slate-200">
                      {activeModalOrder.order?.paymentStatus || "PAID"}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-6 py-3.5 bg-slate-50 rounded-b-2xl dark:border-white/10 dark:bg-slate-800/80">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-600 dark:text-slate-300">
                  Update Status:
                </span>
                <select
                  value={activeModalOrder.status}
                  onChange={(e) => setStatus(activeModalOrder.id, e.target.value)}
                  className="h-8 rounded-lg border border-slate-300 bg-white px-2.5 text-xs font-bold text-slate-800 outline-none focus:border-[#087DB5] dark:border-white/10 dark:bg-slate-700 dark:text-white"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={() => setDetailModalOrder(null)}
                className="rounded-lg bg-slate-200 px-4 py-2 text-xs font-bold text-slate-800 hover:bg-slate-300 dark:bg-white/10 dark:text-slate-200 dark:hover:bg-white/20 transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enlarged Image Lightbox */}
      {enlargedImage && (
        <div
          className="fixed inset-0 z-60 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm animate-in fade-in duration-150 cursor-pointer"
          onClick={() => setEnlargedImage(null)}
        >
          <div
            className="relative max-h-[85vh] max-w-2xl rounded-2xl bg-white p-3 shadow-2xl dark:bg-slate-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-white/10">
              <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate pr-4">
                {enlargedImage.title}
              </p>
              <button
                type="button"
                onClick={() => setEnlargedImage(null)}
                className="rounded-lg p-1 text-slate-400 hover:text-slate-700 dark:hover:text-white cursor-pointer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-3 flex items-center justify-center max-h-[70vh] overflow-hidden rounded-xl bg-slate-50 dark:bg-slate-950 p-2">
              <img
                src={enlargedImage.url}
                alt={enlargedImage.title}
                className="max-h-[65vh] w-auto object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
